import React, { useEffect, useState } from "react";
import "./ProjectDetails.css";
import { getAllDataWithFlag } from "../../../services/CommonService";
import { imgURL } from "../../Config";
import { Link, useParams } from "react-router-dom";
import moment from 'moment';
import EventPage from "../../../pages/EventPage";
import GalleryPage from "../../../pages/GalleryPage";
import Publications from "../Publications/Publications";
import Swal from "sweetalert2";
import ProjectLatLongPreview from "../../Admin/Dashboard/Project/ProjectLatLongPreview";

const normalizeArrayValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (value === null || value === undefined || value === '') {
    return [];
  }

  return String(value)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const normalizeLocations = (locations) => {
  if (!Array.isArray(locations)) {
    return [];
  }

  return locations
    .map((location) => {
      const lat = Number(location?.latitude ?? location?.lat);
      const lng = Number(location?.longitude ?? location?.lng);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      return { lat, lng };
    })
    .filter(Boolean);
};

const getMappedName = (collection, idField, labelField, id) => (
  collection?.find((item) => String(item?.[idField]) === String(id))?.[labelField]
);

const ProjectDetails = () => {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const { id } = useParams();

  useEffect(() => {
    fetchProjects();
  }, [id]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const response = await getAllDataWithFlag(5);
      const projectData = response?.data?.project || [];
      const activeProjects = projectData.filter((item) => item?.is_deleted !== 1 && item?.is_deleted !== '1');
      const filteredData = activeProjects.find((item) => String(item?.id) === String(id));

      if (!filteredData) {
        setProject(null);
        setErrorMessage('Project details could not be found.');
        return;
      }

      const typologyData = JSON.parse(localStorage.getItem('typologyData') || '[]');
      const themeData = JSON.parse(localStorage.getItem('themeData') || '[]');
      const masterData = JSON.parse(localStorage.getItem('StateMaster') || '{}');

      const stateIds = normalizeArrayValue(filteredData?.state);
      const districtIds = normalizeArrayValue(filteredData?.district);
      const typologyIds = normalizeArrayValue(filteredData?.typology);
      const themeIds = normalizeArrayValue(filteredData?.theme);

      const statenames = stateIds
        .map((stateId) => getMappedName(masterData?.state, 'id_mststate', 'statename', stateId))
        .filter(Boolean);
      const districtnames = districtIds
        .map((districtId) => getMappedName(masterData?.district, 'id_mstdistrict', 'districtname', districtId))
        .filter(Boolean);
      const typologynames = typologyIds
        .map((typologyId) => getMappedName(typologyData, 'id', 'typology', typologyId))
        .filter(Boolean);
      const themenames = themeIds
        .map((themeId) => getMappedName(themeData, 'id', 'theme', themeId))
        .filter(Boolean);

      const stateDistrictMap = new Map();

      districtIds.forEach((districtId) => {
        const district = masterData?.district?.find((item) => String(item?.id_mstdistrict) === String(districtId));

        if (!district) {
          return;
        }

        const stateId = String(district.id_mststate);

        if (!stateDistrictMap.has(stateId)) {
          stateDistrictMap.set(stateId, {
            stateName: getMappedName(masterData?.state, 'id_mststate', 'statename', stateId) || '',
            districts: []
          });
        }

        stateDistrictMap.get(stateId).districts.push(district.districtname);
      });

      if (!stateDistrictMap.size && statenames.length) {
        stateIds.forEach((stateId) => {
          stateDistrictMap.set(String(stateId), {
            stateName: getMappedName(masterData?.state, 'id_mststate', 'statename', stateId) || '',
            districts: []
          });
        });
      }

      setProject({
        ...filteredData,
        locations: normalizeLocations(filteredData?.locations),
        statenames,
        districtnames,
        typologynames,
        themenames,
        projectArea: Array.from(stateDistrictMap.entries()).map(([stateId, value]) => ({
          stateId,
          ...value
        }))
      });
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Unable to load project details.';
      setProject(null);
      setErrorMessage(message);
      Swal.fire({
        title: "Error",
        text: message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const banners = JSON.parse(localStorage.getItem('banners') || '[]');
  const bannerItem = banners.find((item) => String(item.banner_type) === '5');
  const dateFormat = (date) => date ? moment(date).format('DD-MM-YYYY') : 'N/A';

  return (
    <div className="user-panel">
      <div className="position-relative">
        <img
          src={bannerItem?.image_path ? `${imgURL}${bannerItem?.image_path}` : '/assets/images/bannerDefault.jpg'}
          alt="Groundwater"
          className="img-fluid w-100"
          style={{ minHeight: '350px', maxHeight: '350px', objectFit: 'cover' }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
          }}
        />
        <div className="container-fluid text-end z-1 position-absolute mt-3">
          <Link to={'../'}><button className='btn btn-sm btn-warning'><i className="bi bi-arrow-left me-1"></i>Back</button></Link>
        </div>
        <div className="position-absolute top-0 left-0 w-100 h-100 bg-dark" style={{ opacity: 0 }}></div>
        <div className="container position-relative" style={{ marginTop: '-65px' }}>
          <div className="bg-white rounded-3 shadow p-4 banner_overlay ">
            <h3 className="text-center mb-4 fw-bold text-truncate">{project?.title || 'Project details'}</h3>
            <div className="text-center">
              <div className='eventtheme_layout'>
                <span className="px-4 py-2">
                  Theme: {project?.themenames?.length ? project.themenames.join(', ') : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container my-5">
        {loading ? (
          <div className="text-center">Loading project details...</div>
        ) : !project ? (
          <div className="alert alert-warning mb-0">{errorMessage || 'Project details could not be found.'}</div>
        ) : (
          <>
            <div className="row">
              <div className="col-lg-8 card p-3">
                {project?.typologynames?.length > 0 && <h2 className="fw-bold mb-4">Typology</h2>}
                <p className="ms-2">{project?.typologynames?.length ? project.typologynames.join(', ') : 'N/A'}</p>
                {project.project_head_details && (<><h2 className="fw-bold mb-4">Project Head Details</h2>
                  <p className="ms-2">{project.project_head_details}</p></>)}
                {project.donor && (<>
                  <h2 className="fw-bold mb-4">Donor</h2>
                  <p className="ms-2">{project.donor}</p>
                </>)}
                {project?.description && <h2 className="fw-bold mb-4">Descriptions</h2>}
                <p className="mb-3 ms-2" dangerouslySetInnerHTML={{ __html: project?.description || '' }}>
                </p>
                <h2 className="fw-bold mb-4">Project Area</h2>
                <table className="table table-bordered">
                  <thead className="adminCardHeader">
                    <tr>
                      <th>State</th>
                      <th>District(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.projectArea?.length ? project.projectArea.map((item) => (
                      <tr key={item.stateId}>
                        <td>{item.stateName || 'N/A'}</td>
                        <td>{item.districts?.length ? item.districts.join(', ') : 'N/A'}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="2" className="text-center">No project area available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="col-lg-4">
                <div className="card mb-4 text-white" style={{ background: '#122B50' }}>
                  <div className="card-header border-0">
                    <h4 className="mb-0">Project Details</h4>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-warning text-white rounded p-2 me-3">
                        <img src='/assets/images/event-date.png' className='eventImgIcon' alt="Project duration" />
                      </div>
                      <div>
                        <strong>{dateFormat(project.start_date)} To {dateFormat(project.end_date)}</strong>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-warning text-white rounded p-2 me-3">
                        <img src='/assets/images/event-time.png' className='eventImgIcon' alt="Project coordinates" />
                      </div>
                      <div>
                        {project?.locations?.length > 0 ? (
                          project.locations.map((location, index) => (
                            <div key={index}>
                              <strong>{location.lat}, {location.lng}</strong>
                            </div>
                          ))
                        ) : (
                          <strong>No coordinates</strong>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-warning text-white rounded p-2 me-3">
                        <img src='/assets/images/event-location.png' className='eventImgIcon' alt="Project locations" />
                      </div>
                      <div>
                        <strong>{project?.statenames?.length ? project.statenames.join(', ') : 'N/A'}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ProjectLatLongPreview
              locations={project?.locations || []}
            />
            <br />
            <EventPage projectId={id} />
            <GalleryPage projectId={id} />
            <Publications projectId={id} />
            <div className="row">
              <div className="col-lg-8"></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectDetails;
